import { TRON_NATIVE_SYMBOL } from "../helpers/tronV2Filters.js"
import { CurrencyActivityRow, toCurrencyActivity } from "../helpers/currencyActivity.js"

// Address statistics on Bitquery GraphQL v2 (v1 no longer serves Tron).
// Counts cover successful transfers of every kind (TRX incl. internal, TRC-10,
// TRC-20) — the same set the tracer follows. `toAddressInfo` reshapes the
// answer into the v1 layout the rest of the app reads.
const addressInfoQuery = `query ($address: String!) {
    Tron(dataset: combined) {
      balance: Balances(
        where: {Balance: {Address: {is: $address}}, Currency: {Native: true}}
      ) {
        Balance {
          Amount
          AmountInUSD
        }
      }
      ins: Transfers(where: {Transfer: {Receiver: {is: $address}, Success: true}}) {
        count
        Block {
          min_date: Time(minimum: Block_Time)
          max_date: Time(maximum: Block_Time)
        }
      }
      currencyIn: Transfers(
        where: {Transfer: {Receiver: {is: $address}, Success: true}}
        orderBy: {descendingByField: "count"}
        limit: {count: 200}
      ) {
        count
        usd: sum(of: Transfer_AmountInUSD)
        Block {
          first_date: Time(minimum: Block_Time)
          last_date: Time(maximum: Block_Time)
        }
        Transfer {
          Currency {
            Symbol
            SmartContract
            AssetId
            Native
          }
        }
      }
      outs: Transfers(where: {Transfer: {Sender: {is: $address}, Success: true}}) {
        count
        Block {
          min_date: Time(minimum: Block_Time)
          max_date: Time(maximum: Block_Time)
        }
      }
      currencyOut: Transfers(
        where: {Transfer: {Sender: {is: $address}, Success: true}}
        orderBy: {descendingByField: "count"}
        limit: {count: 200}
      ) {
        count
        usd: sum(of: Transfer_AmountInUSD)
        Block {
          first_date: Time(minimum: Block_Time)
          last_date: Time(maximum: Block_Time)
        }
        Transfer {
          Currency {
            Symbol
            SmartContract
            AssetId
            Native
          }
        }
      }
    }
  }`

interface V2Counts {
    count: string | number
    Block: { min_date: string | null, max_date: string | null }
}

interface V2CurrencyCount extends CurrencyActivityRow {
    count: string | number
    Transfer: { Currency: { Symbol: string, SmartContract: string, AssetId: string, Native: boolean } }
}

export interface TronAddressInfoV2Response {
    Tron: {
        balance: Array<{ Balance: { Amount: string, AmountInUSD: string } }>
        ins: V2Counts[]
        outs: V2Counts[]
        currencyIn: V2CurrencyCount[]
        currencyOut: V2CurrencyCount[]
    }
}

const toCounts = (rows: V2Counts[]) => ({
    count: Number(rows?.[0]?.count ?? 0),
    min_date: rows?.[0]?.Block?.min_date ?? null,
    max_date: rows?.[0]?.Block?.max_date ?? null,
})

// The form picks a currency by `address`, or by `tokenId` / symbol when the
// address is "-": TRC-20 → contract, TRC-10 → token id, TRX → "TRX" (the
// tracer maps each back to its v2 filter, see tronV2Filters.ts).
const toCurrencies = (rows: V2CurrencyCount[]) => (rows ?? []).map((row) => {
    const { Currency } = row.Transfer
    return {
        count: Number(row.count),
        ...toCurrencyActivity(row),
        currency: {
            symbol: Currency.Native ? TRON_NATIVE_SYMBOL : Currency.Symbol,
            address: Currency.Native || !Currency.SmartContract ? "-" : Currency.SmartContract,
            tokenId: Currency.Native ? "" : Currency.AssetId ?? "",
        },
    }
})

const tron = {
    name: 'tron',
    title: 'TRON Mainnet',
    currency: 'TRX',
    check: true,
    regexp: '([\\W]|^)(T[\\w]{33})([\\W]|$)',
    noregxp: [
        '([\\W]|^)(bnb[0-9a-z]{2})([\\W]|$)',
        '([\\W]|^)(bnb[0-9a-z]{2)([\\W]|$)',
        '([\\W]|^)(T[\\w]{2})([\\W]|$)',
        '([\\W]|^)((cosmos1|terra1|tcro1|tcrocncl1)[a-zA-HJ-NP-Z0-9]{38,70})([\\W]|$)',
    ],
    graphqlV2: true,
    // base58: must reach Bitquery exactly as typed
    caseSensitiveAddress: true,
    query: addressInfoQuery,
    toAddressInfo: ({ Tron }: TronAddressInfoV2Response) => {
        const balance = Tron.balance?.[0]?.Balance
        return {
            tron: {
                address: {
                    balance: Number(balance?.Amount ?? 0),
                    balance_usd: Number(balance?.AmountInUSD ?? 0),
                },
                ins: toCounts(Tron.ins),
                currencyIn: toCurrencies(Tron.currencyIn),
                outs: toCounts(Tron.outs),
                currencyOut: toCurrencies(Tron.currencyOut),
            },
        }
    },
    checkAddress: 'TUD4YXYdj2t1gP5th3A7t97mx1AUmrrQRt',
}
export { tron }
